import * as React from 'react'

import styled from 'styled-components'

import { Accordion, AccordionProps } from '../Accordion'
import { ContentList, OnToggle, OnSelectionChange, MAX_LIST_LENGTH } from './ContentList'
import * as icons from '../../svg/icons'
import * as S from '../../styles'
import { Tag } from '../Tag'
import { FiltersType, useFilterList } from '../../hooks/useFilterList'

export type FilterProps = Pick<AccordionProps, 'title'> & {
    filters?: FiltersType[]
    initialFilters?: FiltersType[]
    onSelectionChange?: OnSelectionChange
    onToggle: OnToggle
    open: boolean
    title: React.ReactNode
}

function FilterComponent({
    filters,
    initialFilters = [],
    onSelectionChange = () => {
        /* noop */
    },
    onToggle,
    open = false,
    title
}: FilterProps): React.ReactElement {
    if (process.env.NODE_ENV !== 'production') {
        React.useEffect(() => {
            if (filters !== undefined && initialFilters.length > 0) {
                console.error(
                    new Error(
                        '<Filter> is both controlled and uncontrolled, use either filters or initialFilters'
                    )
                )
            }
        }, [])
    }

    if (filters !== undefined) {
        return (
            <ControlledFilter
                filters={filters}
                title={title}
                onSelectionChange={onSelectionChange}
                open={open}
                onToggle={onToggle}
            />
        )
    } else {
        return (
            <UncontrolledFilter
                initialFilters={initialFilters}
                title={title}
                onSelectionChange={onSelectionChange}
                open={open}
                onToggle={onToggle}
            />
        )
    }
}
export const Filter = styled(FilterComponent)``

type ControlledFilterProps = Pick<FilterProps, 'title'> & {
    filters: FiltersType[]
    onSelectionChange: OnSelectionChange
    open: boolean
    onToggle: OnToggle
}
function ControlledFilter({
    filters,
    onSelectionChange,
    open,
    onToggle,
    title
}: ControlledFilterProps) {
    const formattedTitle = getTitle(filters, open, title)
    const [shouldShowAll, setShouldShowAll] = React.useState(
        filters.length <= MAX_LIST_LENGTH
    )

    const selectedTag = filters.filter(tag => {
        return tag.isSelected
    })

    return (
        <S.filter.AccordionWrapper>
            <Accordion
                isOpen={open}
                onToggle={() => {
                    onToggle(!open)
                    setShouldShowAll(filters.length <= MAX_LIST_LENGTH)
                }}
                title={formattedTitle}
            >
                <ContentList
                    shouldShowAll={shouldShowAll}
                    setShouldShowAll={setShouldShowAll}
                    onToggle={onToggle}
                    filters={filters}
                    title={title}
                    onSelectionChange={onSelectionChange}
                />
            </Accordion>
            <icons.ChevronRight size={20} />
            {!open && selectedTag.length > 0 && (
                <S.filter.TagList>
                    {selectedTag.map(tag => {
                        return (
                            <Tag
                                key={tag.id}
                                onClose={() => {
                                    onSelectionChange(tag.id, false)
                                }}
                            >
                                {tag.name}
                            </Tag>
                        )
                    })}
                </S.filter.TagList>
            )}
        </S.filter.AccordionWrapper>
    )
}

type UncontrolledFilterProps = Pick<FilterProps, 'title'> & {
    initialFilters: FiltersType[]
    onSelectionChange: OnSelectionChange
    onToggle: OnToggle
    open: boolean
}
function UncontrolledFilter({
    initialFilters,
    onSelectionChange,
    onToggle,
    open,
    title
}: UncontrolledFilterProps) {
    const { controlledFilters, updateFilter } = useFilterList(initialFilters)

    return (
        <ControlledFilter
            filters={controlledFilters}
            title={title}
            onSelectionChange={(val: string, status: boolean) => {
                updateFilter(val, status)
                onSelectionChange(val, status)
            }}
            onToggle={onToggle}
            open={open}
        />
    )
}

function getTitle(
    filters: FiltersType[],
    isOpen: boolean,
    title: React.ReactNode
): React.ReactNode {
    const numberSelected = filters.filter(filter => filter.isSelected).length

    if (numberSelected > 0 && !isOpen) {
        return (
            <>
                {title}
                {` (${numberSelected})`}
            </>
        )
    } else {
        return title
    }
}
